import React, { useEffect, useRef, useState } from "react";
import Breadcrumb from "@/components/site/Breadcrumb";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { extractId, getSchoolUrl } from "@/lib/slug";
import SEO from "@/components/site/SEO";
import JsonLd, { discussionForumPostingSchema } from "@/components/site/JsonLd";
import { ForumRichEditor, HtmlContent } from "@/components/site/ForumRichEditor";
import api from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Pin, Move, X, Bookmark, BookmarkCheck, Newspaper, ArrowRight, School as SchoolIcon } from "lucide-react";
import { toast } from "sonner";
import UserLevelBadge from "@/components/site/UserLevelBadge";
import FoundingParentBadge from "@/components/site/FoundingParentBadge";
import PostActionBar from "@/components/site/PostActionBar";
import QuoteBlock, { DeletedQuoteBlock } from "@/components/site/QuoteBlock";
import ReportDialog from "@/components/site/ReportDialog";
import ModActionsMenu from "@/components/site/ModActionsMenu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { promptLogin, promptRegister } from "@/lib/authRedirect";

export default function ThreadDetail() {
  const { id: idParam } = useParams();
  const id = extractId(idParam, "thr");
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [data, setData] = useState({ thread: null, replies: [] });
  const [body, setBody] = useState("");
  const [quoting, setQuoting] = useState(null); // { type, reply_id?, thread_id?, user_name, created_at, excerpt, title? }
  const [submitting, setSubmitting] = useState(false);
  const [replyPhase, setReplyPhase] = useState("idle"); // idle | sending | sent
  const [replyResetKey, setReplyResetKey] = useState(0);
  const replySentTimerRef = React.useRef(null);
  React.useEffect(() => () => { if (replySentTimerRef.current) clearTimeout(replySentTimerRef.current); }, []);
  // Contextual moderation (mod-only edit/delete on thread head + each reply)
  const [editThreadOpen, setEditThreadOpen] = useState(false);
  const [editThreadTitle, setEditThreadTitle] = useState("");
  const [editThreadBody, setEditThreadBody] = useState("");
  const [editThreadKey, setEditThreadKey] = useState(0);
  const [editThreadSaving, setEditThreadSaving] = useState(false);
  const [editReplyId, setEditReplyId] = useState(null);
  const [editReplyBody, setEditReplyBody] = useState("");
  const [editReplyKey, setEditReplyKey] = useState(0);
  const [editReplySaving, setEditReplySaving] = useState(false);
  // Move-to-Ruang (legacy) — reuses PATCH /admin/threads/{id}/ruang + same Select
  // pattern DiscussionDetail uses for native. No parallel move system.
  const [moveRuangOpen, setMoveRuangOpen] = useState(false);
  const [moveRuangTarget, setMoveRuangTarget] = useState("");
  const [ruangList, setRuangList] = useState([]);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState("");
  const [subForums, setSubForums] = useState([]);
  const [highlightedReplyId, setHighlightedReplyId] = useState(null);
  const [bookmarked, setBookmarked] = useState(false);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [bookmarkBusy, setBookmarkBusy] = useState(false);
  // Report dialog state
  const [reportOpen, setReportOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState(null); // { type, id }
  const openReport = (type, id) => {
    if (!user) { promptLogin(nav, "Masuk untuk melaporkan konten ini."); return; }
    setReportTarget({ type, id });
    setReportOpen(true);
  };
  const textRef = useRef(null);
  const isMod = user && ["moderator", "admin", "superadmin"].includes(user.role);

  // Deep-link: if URL hash matches #reply-<id>, scroll into view + briefly highlight.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash || "";
    const m = hash.match(/^#reply-([\w-]+)$/);
    if (!m) return;
    const replyId = m[1];
    if (!data.replies?.some((r) => r.reply_id === replyId)) return;
    setHighlightedReplyId(replyId);
    const t1 = setTimeout(() => {
      const el = document.getElementById(`reply-${replyId}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 250);
    const t2 = setTimeout(() => setHighlightedReplyId(null), 3500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [data.replies]);

  const load = () => api.get(`/threads/${id}`).then((r) => setData(r.data));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  // Cross-Discovery: pull journal articles related to this thread's school / tags
  useEffect(() => {
    if (!id) return;
    api.get(`/threads/${id}/related-articles`, { params: { limit: 3 } })
      .then((r) => setRelatedArticles(r.data || []))
      .catch(() => setRelatedArticles([]));
  }, [id]);

  // If Forum feed sent us with ?quote=head, auto-open the quote composer once the thread loads
  useEffect(() => {
    if (!data.thread) return;
    if (sp.get("quote") === "head") {
      const t = data.thread;
      setQuoting({ type: "thread", thread_id: t.thread_id, user_name: t.user_name, created_at: t.created_at, excerpt: (t.body || "").slice(0, 220), title: t.title });
      sp.delete("quote");
      setSp(sp, { replace: true });
      setTimeout(() => textRef.current?.focus(), 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.thread]);

  useEffect(() => {
    if (!isMod) return;
    api.get("/forum/categories").then((r) => {
      const flat = [];
      (r.data || []).forEach((c) => (c.sub_forums || []).forEach((s) => flat.push({ ...s, category_label: c.label })));
      setSubForums(flat);
    }).catch(() => {});
  }, [isMod]);

  const moveThread = async () => {
    try {
      await api.patch(`/admin/threads/${id}/move`, { sub_forum_id: moveTarget || null });
      toast.success("Thread dipindahkan");
      setMoveOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  // Load current-user's bookmark state for this thread
  useEffect(() => {
    if (!user || !id) { setBookmarked(false); return; }
    api.get("/me/bookmarks/thread-ids")
      .then((r) => setBookmarked((r.data?.ids || []).includes(id)))
      .catch(() => {});
  }, [user, id]);

  const toggleBookmark = async () => {
    if (!user) { promptLogin(nav); return; }
    if (bookmarkBusy) return;
    setBookmarkBusy(true);
    // Optimistic
    const next = !bookmarked;
    setBookmarked(next);
    try {
      const r = await api.post(`/threads/${id}/bookmark/toggle`);
      const isBm = !!r.data?.bookmarked;
      setBookmarked(isBm);
      toast.success(isBm ? "Diskusi tersimpan" : "Simpanan dihapus");
    } catch {
      setBookmarked(!next); // rollback
      toast.error("Gagal menyimpan diskusi");
    } finally {
      setBookmarkBusy(false);
    }
  };

  const upvote = async () => {
    if (!user) { promptLogin(nav); return; }
    try { await api.post(`/threads/${id}/upvote`); load(); }
    catch (_) { toast.error("Gagal suka"); }
  };

  const likeReply = async (r) => {
    if (!user) { promptLogin(nav); return; }
    try {
      const res = await api.post(`/replies/${r.reply_id}/like`);
      // Optimistic-ish: patch the local reply in place
      setData((prev) => ({
        ...prev,
        replies: prev.replies.map((x) => x.reply_id === r.reply_id
          ? { ...x, likes: res.data.likes, liked_by: res.data.liked ? [...(x.liked_by || []), user.user_id] : (x.liked_by || []).filter((u) => u !== user.user_id) }
          : x),
      }));
    } catch (_) { toast.error("Gagal suka"); }
  };

  const openQuote = (target) => {
    if (!user) { promptLogin(nav); return; }
    setQuoting(target);
    setTimeout(() => textRef.current?.focus(), 60);
  };

  const focusReply = () => {
    if (!user) { promptLogin(nav); return; }
    textRef.current?.focus();
    textRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const reply = async (e) => {
    e.preventDefault();
    if (!user) { promptLogin(nav); return; }
    // Double-click / repeated-submit guard: any non-idle phase blocks re-submit.
    if (!body.trim() || replyPhase !== "idle") return;
    setSubmitting(true);
    setReplyPhase("sending");
    try {
      const payload = { thread_id: id, body: body.trim() };
      if (quoting?.type === "reply" && quoting.reply_id) payload.quoted_reply_id = quoting.reply_id;
      if (quoting?.type === "thread" && quoting.thread_id) payload.quoted_thread_id = quoting.thread_id;
      const r = await api.post("/replies", payload);
      // Append instantly — Threads-style, no full-page refresh
      setData((prev) => ({
        ...prev,
        replies: [...prev.replies, r.data],
        thread: prev.thread ? { ...prev.thread, reply_count: (prev.thread.reply_count || 0) + 1 } : prev.thread,
      }));
      // Clear editor state (bump resetKey to force TipTap to visually clear).
      setBody("");
      setQuoting(null);
      setReplyResetKey((k) => k + 1);
      // Post → Mengirim… → Terkirim ✓ → Post
      setReplyPhase("sent");
      replySentTimerRef.current = setTimeout(() => {
        setReplyPhase("idle");
        replySentTimerRef.current = null;
      }, 1200);
    } catch (err) {
      // Don't clear the draft on failure — keep the user's text.
      toast.error(err.response?.data?.detail || "Gagal membalas. Coba lagi.");
      setReplyPhase("idle");
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Moderation handlers (mod-only; backend re-enforces) ----
  const beginEditThread = () => {
    if (!data.thread) return;
    setEditThreadTitle(data.thread.title || "");
    setEditThreadBody(data.thread.body || "");
    setEditThreadOpen(true);
  };
  const saveEditThread = async () => {
    if (editThreadSaving) return;
    const title = (editThreadTitle || "").trim();
    const body = (editThreadBody || "").trim();
    if (title.length < 5) { toast.error("Judul minimal 5 karakter."); return; }
    if (!body) { toast.error("Isi tidak boleh kosong."); return; }
    setEditThreadSaving(true);
    try {
      await api.patch(`/admin/threads/${id}/edit`, { title, body });
      setData((prev) => ({ ...prev, thread: { ...prev.thread, title, body, edited_at: new Date().toISOString() } }));
      toast.success("Thread diperbarui.");
      setEditThreadOpen(false);
      setEditThreadKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan.");
    } finally { setEditThreadSaving(false); }
  };
  const deleteThreadAsMod = async () => {
    if (!window.confirm("Hapus thread ini beserta seluruh balasannya? Tindakan tidak dapat dibatalkan.")) return;
    try {
      await api.delete(`/admin/threads/${id}`);
      toast.success("Thread dihapus.");
      nav(data.thread?.ruang_slug ? `/forum/ruang/${data.thread.ruang_slug}` : "/forum", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus.");
    }
  };
  const beginEditReply = (r) => {
    setEditReplyId(r.reply_id);
    setEditReplyBody(r.body || "");
  };
  const cancelEditReply = () => {
    setEditReplyId(null);
    setEditReplyKey((k) => k + 1);
  };
  const saveEditReply = async () => {
    if (editReplySaving || !editReplyId) return;
    const b = (editReplyBody || "").trim();
    if (!b) { toast.error("Isi tidak boleh kosong."); return; }
    setEditReplySaving(true);
    try {
      await api.patch(`/admin/replies/${editReplyId}/edit`, { body: b });
      setData((prev) => ({
        ...prev,
        replies: prev.replies.map((x) => x.reply_id === editReplyId ? { ...x, body: b, edited_at: new Date().toISOString() } : x),
      }));
      toast.success("Balasan diperbarui.");
      setEditReplyId(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan.");
    } finally { setEditReplySaving(false); }
  };
  const deleteReplyAsMod = async (r) => {
    if (!window.confirm("Hapus balasan ini?")) return;
    try {
      await api.delete(`/admin/replies/${r.reply_id}`);
      setData((prev) => ({
        ...prev,
        replies: prev.replies.filter((x) => x.reply_id !== r.reply_id),
        thread: prev.thread ? { ...prev.thread, reply_count: Math.max(0, (prev.thread.reply_count || 0) - 1) } : prev.thread,
      }));
      toast.success("Balasan dihapus.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus.");
    }
  };

  const openMoveRuangLegacy = async () => {
    if (!ruangList.length) {
      try {
        const r = await api.get("/ruang");
        const flat = [...(r.data?.grup_a?.items || []), ...(r.data?.grup_b?.items || [])]
          .filter((x) => x.is_active !== false);
        setRuangList(flat);
      } catch { toast.error("Gagal memuat daftar Ruang."); return; }
    }
    setMoveRuangTarget(data.thread?.ruang_id || "");
    setMoveRuangOpen(true);
  };
  const submitMoveRuangLegacy = async () => {
    if (!moveRuangTarget || moveRuangTarget === data.thread?.ruang_id) { setMoveRuangOpen(false); return; }
    try {
      const chosen = ruangList.find((r) => r.ruang_id === moveRuangTarget);
      await api.patch(`/admin/threads/${id}/ruang`, { ruang_id: moveRuangTarget });
      // Reflect breadcrumb + chip + back-link immediately from the chosen Ruang metadata.
      setData((prev) => ({
        ...prev,
        thread: {
          ...prev.thread,
          ruang_id: moveRuangTarget,
          ruang_slug: chosen?.slug,
          ruang_name: chosen?.name,
          ruang_icon: chosen?.icon,
        },
      }));
      toast.success(`Dipindahkan ke ${chosen?.name}.`);
      setMoveRuangOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memindahkan.");
    }
  };

  if (!data.thread) return <div className="max-w-3xl mx-auto p-10 text-[#4B5563]">Memuat…</div>;
  const t = data.thread;
  const threadOrigin = typeof window !== "undefined" ? `${window.location.origin}/forum/${t.thread_id}` : "";
  const upvotedByMe = !!(user && (t.upvoted_by || []).includes(user.user_id));

  return (
    <div data-testid="thread-detail-page" className="max-w-3xl mx-auto px-6 lg:px-10 py-10">
      {(() => {
        const title = `${t.title} | Forum ${t.school_name || "The School Reports"}`;
        const excerpt = (t.body || "").replace(/\s+/g, " ").trim().slice(0, 160);
        const desc = excerpt || `Diskusi orang tua & alumni di forum ${t.category || "The School Reports"}. Bergabunglah untuk membaca pendapat komunitas.`;
        const canonical = typeof window !== "undefined" ? `${window.location.origin}/forum/${t.thread_id}` : "";
        return (
          <>
            <SEO title={title} description={desc} type="article" canonical={canonical} />
            <JsonLd data={discussionForumPostingSchema(t, data.replies || [], canonical)} />
          </>
        );
      })()}
      <Breadcrumb testid="thread-breadcrumb" items={[
        { label: "Forum", to: "/forum" },
        // When mapped to a Ruang, breadcrumb points to that Ruang. Legacy sub-forum
        // trail is intentionally hidden — the unified UI has no notion of it.
        ...(t.ruang_slug ? [{ label: t.ruang_name || t.ruang_slug, to: `/forum/ruang/${t.ruang_slug}` }] : []),
        { label: t.title.length > 40 ? t.title.slice(0, 40) + "…" : t.title },
      ]} />
      <Link
        to={t.ruang_slug ? `/forum/ruang/${t.ruang_slug}` : "/forum"}
        className="text-sm text-[#4B5563] hover:text-[#E07A5F] mt-3 inline-block"
        data-testid="back-to-forum"
      >← {t.ruang_slug ? (t.ruang_name || "Kembali ke Ruang") : "Kembali ke Forum"}</Link>
      {user && (
        <button
          type="button"
          onClick={toggleBookmark}
          disabled={bookmarkBusy}
          data-testid="thread-bookmark-btn"
          aria-pressed={bookmarked}
          title={bookmarked ? "Batal simpan" : "Simpan diskusi"}
          className={
            "ml-3 mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs transition-colors " +
            (bookmarked
              ? "border border-[#D2673E] bg-[#FDF6EF] text-[#BE5730] hover:bg-[#F6E7DF]"
              : "border border-[#EAE7E0] bg-white text-[#4B5563] hover:border-[#D2673E] hover:text-[#D2673E]")
          }
        >
          {bookmarked
            ? <><BookmarkCheck size={12} strokeWidth={2.25} /> Tersimpan</>
            : <><Bookmark size={12} strokeWidth={2} /> Simpan</>}
        </button>
      )}
      {isMod && !t.ruang_id && (
        <button type="button" onClick={() => { setMoveTarget(t.sub_forum_id || ""); setMoveOpen(true); }}
          data-testid="thread-move-btn"
          className="ml-3 mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-[#EAE7E0] bg-white text-xs text-[#4B5563] hover:border-[#1E3A8A] hover:text-[#1E3A8A]">
          <Move size={12} /> Pindah ke sub-forum
        </button>
      )}
      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent data-testid="thread-move-dialog">
          <DialogHeader><DialogTitle>Pindahkan thread</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-[#4B5563]">Pilih sub-forum tujuan. Kategori parent akan menyesuaikan otomatis. Author akan mendapat notifikasi.</div>
            <Select value={moveTarget} onValueChange={setMoveTarget}>
              <SelectTrigger data-testid="thread-move-select"><SelectValue placeholder="Pilih sub-forum tujuan…" /></SelectTrigger>
              <SelectContent>
                {subForums.map((s) => (
                  <SelectItem key={s.sub_forum_id} value={s.sub_forum_id}>{s.category_label} › {s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setMoveOpen(false)} className="rounded-full h-9">Batal</Button>
              <Button disabled={!moveTarget} onClick={moveThread} data-testid="thread-move-submit" className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-9">Pindahkan</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Thread head */}
      <div className="mt-4 bg-white border border-[#EAE7E0] rounded-2xl p-6" data-testid="thread-head">
        <div className="flex items-center gap-2 flex-wrap">
          {/* When mapped to a Ruang, show the Ruang chip instead of legacy category
              (UMUM / sub-forum / etc.) so the UI mirrors DiscussionDetail. */}
          {t.ruang_slug ? (
            <Link to={`/forum/ruang/${t.ruang_slug}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#EEF2FF] text-[#1E3A8A] text-[11px] font-semibold" data-testid="thread-ruang-chip">
              <span aria-hidden>{t.ruang_icon || "🏫"}</span> {t.ruang_name}
            </Link>
          ) : (
            <span className="pill">{t.category}</span>
          )}
          {t.pinned && (
            <span data-testid="thread-detail-pinned-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FED7AA] text-[#9A3412] text-[10px] uppercase tracking-wider font-semibold">
              <Pin size={10} /> Dipinkan
            </span>
          )}
        </div>
        <h1 className="font-sans font-bold text-3xl lg:text-4xl text-[#1F2937] mt-3 leading-tight tracking-tight">{t.title}</h1>
        <div className="mt-3 text-xs text-[#4B5563] inline-flex items-center gap-2 flex-wrap">
          <span> {t.user_name}</span>
          <UserLevelBadge name={t.user_level_name} order={t.user_level_order} testid="thread-author-level" />
          {t.is_founding_parent && <FoundingParentBadge testid="thread-author-founding" />}
          <span>· {new Date(t.created_at).toLocaleDateString("id-ID")}</span>
          {t.edited_at && <span className="text-[10px] text-[#9CA3AF] italic" title={new Date(t.edited_at).toLocaleString("id-ID")}>· diedit</span>}
          {isMod && (
            <ModActionsMenu
              testidPrefix="thread-mod-menu"
              onEdit={beginEditThread}
              onMove={openMoveRuangLegacy}
              onDelete={deleteThreadAsMod}
            />
          )}
        </div>
        <HtmlContent
          value={t.body}
          className={"mt-5 leading-relaxed " + (t.is_moderated ? "text-[#4B5563] italic bg-[#F9F8F6] border-l-4 border-[#B91C1C] px-4 py-3 rounded" : "text-[#1F2937]")}
          testid={t.is_moderated ? "thread-moderated-notice" : "thread-body"}
        />
        {/* Threads-style action bar (school badge sits right above it — mirrors the forum feed card layout) */}
        <div className="mt-5 pt-3 border-t border-[#EAE7E0]">
          {t.school_id && t.school_name && (
            <div className="mb-2">
              <Link
                to={getSchoolUrl({ school_id: t.school_id, name: t.school_name }, { query: "tab=diskusi" })}
                data-testid="thread-detail-school-badge"
                className="inline-flex items-center gap-1 pl-1.5 pr-2.5 py-0.5 rounded-full bg-[#F6E7DF]/60 border border-[#F3D9C7] text-[#8A5B3F] hover:bg-[#F6E7DF] hover:border-[#D2673E] hover:text-[#BE5730] transition-colors"
                title={`Lihat diskusi komunitas di ${t.school_name}`}
              >
                <SchoolIcon size={10} strokeWidth={2.5} className="text-[#BE5730]/80 shrink-0" />
                <span className="text-[10px] font-medium tracking-tight truncate max-w-[280px]">
                  #&nbsp;{t.school_name}
                </span>
              </Link>
            </div>
          )}
          <PostActionBar
            testidPrefix="thread"
            liked={upvotedByMe}
            likeCount={t.upvotes || 0}
            replyCount={t.reply_count || 0}
            onLike={upvote}
            onReply={focusReply}
            onQuote={() => openQuote({ type: "thread", thread_id: t.thread_id, user_name: t.user_name, created_at: t.created_at, excerpt: (t.body || "").slice(0, 220), title: t.title })}
            onReport={t.is_moderated ? undefined : () => openReport("thread", t.thread_id)}
            shareUrl={threadOrigin}
            shareText={`${t.title} — Forum The School Reports`}
          />
        </div>
      </div>

      {/* Replies */}
      <div className="mt-8">
        <h2 className="font-sans font-semibold text-xl text-[#1F2937] mb-4" data-testid="replies-heading">Balasan · {data.replies.length}</h2>
        <div className="space-y-3">
          {data.replies.map((r, i) => {
            const liked = !!(user && (r.liked_by || []).includes(user.user_id));
            const shareUrl = `${window.location.origin}/forum/${id}#reply-${r.reply_id}`;
            const shareText = `${r.user_name} di "${t.title}"`;
            return (
              <div
                key={r.reply_id}
                id={`reply-${r.reply_id}`}
                data-testid={`reply-${i}`}
                className={
                  "bg-white border rounded-2xl p-5 transition-all duration-500 scroll-mt-24 " +
                  (highlightedReplyId === r.reply_id
                    ? "border-[#D2673E] ring-4 ring-[#D2673E]/25 shadow-[0_0_0_1px_#D2673E]"
                    : "border-[#EAE7E0]")
                }
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-[#1F2937] inline-flex items-center gap-2 flex-wrap">
                    {r.user_name}
                    <UserLevelBadge name={r.user_level_name} order={r.user_level_order} testid={`reply-${i}-level`} />
                    {r.is_founding_parent && <FoundingParentBadge testid={`reply-${i}-founding`} />}
                    {r.edited_at && <span className="text-[10px] text-[#9CA3AF] italic" title={new Date(r.edited_at).toLocaleString("id-ID")}>· diedit</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-xs text-[#4B5563]">{new Date(r.created_at).toLocaleDateString("id-ID")}</div>
                    {isMod && editReplyId !== r.reply_id && (
                      <ModActionsMenu
                        testidPrefix={`reply-${i}-mod-menu`}
                        onEdit={() => beginEditReply(r)}
                        onDelete={() => deleteReplyAsMod(r)}
                      />
                    )}
                  </div>
                </div>
                {/* Inline quoted-post block */}
                {r.quote_snapshot && <QuoteBlock snapshot={r.quote_snapshot} testid={`reply-${i}-quote`} />}
                {(r.quoted_reply_id || r.quoted_thread_id) && !r.quote_snapshot && <DeletedQuoteBlock testid={`reply-${i}-quote-deleted`} />}
                {editReplyId === r.reply_id ? (
                  <div className="mt-3 space-y-2" data-testid={`reply-${i}-edit-form`}>
                    <ForumRichEditor
                      key={`reply-edit-${r.reply_id}-${editReplyKey}`}
                      value={editReplyBody}
                      onChange={setEditReplyBody}
                      testidPrefix={`reply-${i}-edit`}
                      minHeight={90}
                    />
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="outline" onClick={cancelEditReply} data-testid={`reply-${i}-edit-cancel`} className="rounded-full h-8 text-xs">Batal</Button>
                      <Button onClick={saveEditReply} disabled={editReplySaving} data-testid={`reply-${i}-edit-save`} className="rounded-full bg-[#E07A5F] hover:bg-[#C96D55] text-white h-8 text-xs">
                        {editReplySaving ? "Menyimpan…" : "Simpan"}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <HtmlContent
                    value={r.body}
                    className={"mt-2 leading-relaxed " + (r.is_moderated ? "text-[#4B5563] italic bg-[#F9F8F6] border-l-4 border-[#B91C1C] px-4 py-3 rounded" : "text-[#1F2937]")}
                    testid={r.is_moderated ? `reply-${i}-moderated-notice` : `reply-${i}-body`}
                  />
                )}                <div className="mt-3 pt-2 border-t border-[#F3F1ED]">
                  <PostActionBar
                    testidPrefix={`reply-${i}`}
                    liked={liked}
                    likeCount={r.likes || 0}
                    hideReply={false}
                    onLike={() => likeReply(r)}
                    onReply={focusReply}
                    onQuote={() => openQuote({ type: "reply", reply_id: r.reply_id, user_name: r.user_name, created_at: r.created_at, excerpt: (r.body || "").slice(0, 220) })}
                    onReport={r.is_moderated ? undefined : () => openReport("reply", r.reply_id)}
                    shareUrl={shareUrl}
                    shareText={shareText}
                    size="sm"
                  />
                </div>
              </div>
            );
          })}
          {data.replies.length === 0 && <div className="text-sm text-[#4B5563]" data-testid="replies-empty">Belum ada balasan. Jadilah yang pertama.</div>}
        </div>

        {/* Cross-Discovery: journal articles related to this thread's school / tags */}
        {relatedArticles.length > 0 && (
          <section
            data-testid="thread-related-articles"
            className="mt-8 rounded-2xl border border-[#EAE7E0] bg-white p-5"
          >
            <div className="flex items-center gap-2 mb-1">
              <Newspaper size={16} className="text-[#1E3A8A]" strokeWidth={2.25} />
              <div className="text-xs uppercase tracking-widest text-[#1E3A8A] font-semibold">Artikel The Reports Terkait</div>
            </div>
            <p className="text-sm text-[#5C6472]">
              Review editorial mendalam tentang sekolah atau topik dalam diskusi ini.
            </p>
            <ul className="mt-4 space-y-3">
              {relatedArticles.map((a, i) => (
                <li
                  key={a.slug}
                  data-testid={`thread-related-article-${i}`}
                  className="rounded-xl border border-[#E8E4DB] hover:border-[#1E3A8A] transition-colors"
                >
                  <Link to={`/jurnal/${a.slug}`} className="flex items-start gap-3 p-3 group">
                    {a.cover_url && (
                      <img
                        src={a.cover_url}
                        alt=""
                        className="w-16 h-16 rounded-lg object-cover shrink-0 border border-[#E8E4DB]"
                        loading="lazy"
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap text-[10px] uppercase tracking-widest text-[#8A8F99]">
                        <span className="text-[#1E3A8A] font-semibold">{a.content_type || "insight"}</span>
                        {a.read_time_min ? <span>· {a.read_time_min} mnt baca</span> : null}
                      </div>
                      <div className="mt-1 text-[15px] font-semibold text-[#1B2B4D] group-hover:text-[#D2673E] leading-snug tracking-tight line-clamp-2">
                        {a.title}
                      </div>
                      {a.excerpt && (
                        <p className="mt-1 text-xs text-[#5C6472] line-clamp-2 leading-relaxed">{a.excerpt}</p>
                      )}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/"
              data-testid="thread-related-articles-see-all"
              className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#1E3A8A] hover:text-[#D2673E]"
            >
              Baca lebih banyak di The Reports <ArrowRight size={14} strokeWidth={2.25} />
            </Link>
          </section>
        )}

        {/* Persistent Threads-style quick reply composer */}
        <form
          onSubmit={reply}
          className="mt-6 bg-white border border-[#EAE7E0] rounded-2xl p-4 sticky bottom-3 shadow-sm"
          data-testid="reply-form"
        >
          {quoting && (
            <div className="mb-3 relative" data-testid="composer-quote-preview">
              <QuoteBlock snapshot={quoting} compact />
              <button
                type="button"
                onClick={() => setQuoting(null)}
                data-testid="composer-quote-clear"
                aria-label="Hapus kutipan"
                className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-white border border-[#EAE7E0] text-[#4B5563] hover:text-[#1F2937] flex items-center justify-center shadow-sm"
              >
                <X size={12} />
              </button>
            </div>
          )}
          <ForumRichEditor
            value={body}
            onChange={setBody}
            placeholder={user ? (quoting ? "Tambahkan pikiran Anda pada kutipan ini…" : "Bagikan pikiran Anda…") : "Masuk untuk membalas"}
            testidPrefix="reply"
            minHeight={90}
            resetKey={replyResetKey}
          />
          <div className="mt-3 flex items-center justify-between gap-3">
            <div className="text-[11px] text-[#9CA3AF]">
              {user ? "" : (
                <button
                  type="button"
                  onClick={() => promptLogin(nav)}
                  data-testid="reply-login-prompt"
                  className="text-[#1E3A8A] hover:underline"
                >
                  Masuk untuk membalas
                </button>
              )}
            </div>
            <Button
              type="submit"
              disabled={!user || !body.trim() || replyPhase !== "idle"}
              data-testid="reply-submit-btn"
              className={`rounded-full text-white h-10 px-5 ${
                replyPhase === "sent"
                  ? "bg-[#2E7D5B] hover:bg-[#2E7D5B]"
                  : "bg-[#E07A5F] hover:bg-[#C96D55]"
              }`}
            >
              {replyPhase === "sending" ? "Mengirim…" : replyPhase === "sent" ? "Terkirim ✓" : "Post"}
            </Button>
          </div>
        </form>
      </div>

      {/* Report dialog (thread or reply) */}
      <ReportDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        targetType={reportTarget?.type}
        targetId={reportTarget?.id}
      />

      {/* Thread edit dialog (mod-only) */}
      <Dialog open={editThreadOpen} onOpenChange={setEditThreadOpen}>
        <DialogContent data-testid="thread-edit-dialog" className="max-w-2xl">
          <DialogHeader><DialogTitle>Edit thread</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input
              value={editThreadTitle}
              onChange={(e) => setEditThreadTitle(e.target.value)}
              placeholder="Judul thread"
              data-testid="thread-edit-title"
              className="w-full h-11 rounded-xl border border-[#EAE7E0] px-3 text-lg font-semibold focus:border-[#1E3A8A] focus:outline-none"
            />
            <ForumRichEditor
              key={`thread-edit-${editThreadKey}`}
              value={editThreadBody}
              onChange={setEditThreadBody}
              testidPrefix="thread-edit-body"
              minHeight={180}
            />
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setEditThreadOpen(false)} className="rounded-full h-9">Batal</Button>
              <Button onClick={saveEditThread} disabled={editThreadSaving} data-testid="thread-edit-save" className="rounded-full bg-[#E07A5F] hover:bg-[#C96D55] text-white h-9">
                {editThreadSaving ? "Menyimpan…" : "Simpan"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Move-to-Ruang dialog (legacy thread, mod-only) */}
      <Dialog open={moveRuangOpen} onOpenChange={setMoveRuangOpen}>
        <DialogContent data-testid="thread-move-ruang-dialog">
          <DialogHeader><DialogTitle>Pindahkan thread ke Ruang lain</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-[#4B5563]">
              Hanya field <code className="text-[11px] bg-[#F3F1ED] px-1 rounded">ruang_id</code> yang berubah. Balasan, bookmark, kutipan, dan timestamps tetap.
            </div>
            <Select value={moveRuangTarget} onValueChange={setMoveRuangTarget}>
              <SelectTrigger data-testid="thread-move-ruang-select"><SelectValue placeholder="Pilih Ruang tujuan…" /></SelectTrigger>
              <SelectContent>
                {ruangList.map((r) => (
                  <SelectItem key={r.ruang_id} value={r.ruang_id}>
                    {r.icon} {r.name}{r.ruang_id === data.thread?.ruang_id ? " (Ruang saat ini)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setMoveRuangOpen(false)} className="rounded-full h-9">Batal</Button>
              <Button
                disabled={!moveRuangTarget || moveRuangTarget === data.thread?.ruang_id}
                onClick={submitMoveRuangLegacy}
                data-testid="thread-move-ruang-submit"
                className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-9"
              >
                Pindahkan
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
